/**
 * Custom Hook: useVehicleData
 * Manages vehicle colors, models extraction from spreadsheet, and year ranges
 */

import { useEffect, useState } from "react";
import { VEHICLE_COLORS, YEAR_RANGE } from "../utils/constants/vehicleData";
import FixedAgent from "../utils/api/FixedAgent";
import { showErrorToast } from "../utils/toastFactory";

export const useVehicleData = (
  selectedMakeId,
  makeModelMapping,
  renewData,
  ceiMake,
  niconMake,
  token,
) => {
  const [vehicleColor] = useState(VEHICLE_COLORS);
  const [vehicleModel, setVehicleModel] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Extract models from spreadsheet based on selected make ID
   */
  useEffect(() => {
    const extractModelsFromSpreadsheet = () => {
      if (!selectedMakeId) {
        setVehicleModel([]);
        return;
      }

      // Resolve the Make Name from the hardcoded lists
      const makeArray =
        renewData.insuranceType === "NICON" ? niconMake : ceiMake;
      const selectedMakeObj = makeArray.find((m) => m.id === selectedMakeId);

      if (!selectedMakeObj) {
        setVehicleModel([]);
        return;
      }

      const searchMakeName = selectedMakeObj.name.toLowerCase().trim();

      console.log("[Model Extraction] Selected make:", {
        id: selectedMakeId,
        name: selectedMakeObj.name,
        searchTerm: searchMakeName,
      });

      // Try spreadsheet first
      if (makeModelMapping.data.length > 0) {
        const matchingRows = makeModelMapping.data.filter((row) => {
          const rowMakeName = row["Make Name"]?.toLowerCase().trim();
          return rowMakeName && rowMakeName.includes(searchMakeName);
        });

        console.log(
          `[Model Extraction] Found ${matchingRows.length} matching rows in spreadsheet`,
        );

        if (matchingRows.length > 0) {
          const models = matchingRows.map((row) => {
            const modelId = row["Model ID"];
            const modelName = row["Model Name"];

            if (renewData.insuranceType === "NICON") {
              return {
                id: modelId,
                name: modelName,
              };
            } else {
              return {
                vehicleModelId: modelId,
                modelName: modelName,
              };
            }
          });

          setVehicleModel(models);
          return;
        }
      }

      // Fallback: Fetch from API if spreadsheet didn't load or returned no models
      console.log(
        "[Model Extraction] Spreadsheet data empty or no match, trying API fallback...",
      );

      const fetchFromApi = async () => {
        try {
          if (selectedMakeId) {
            setIsLoading(true);
            const apiRes = await (renewData.insuranceType === "NICON"
              ? FixedAgent.getNiconVehicleModels({
                  id: selectedMakeId,
                  token,
                })
              : FixedAgent.getVehicleModelsByID({
                  id: selectedMakeId,
                  token,
                }));

            const apiModels =
              renewData.insuranceType === "NICON"
                ? apiRes.data.data.model
                : apiRes.data.modelToReturn || [];

            if (Array.isArray(apiModels)) {
              console.log(
                `[Model Extraction] Fetched ${apiModels.length} models from API fallback`,
              );
              setVehicleModel(apiModels);
            }
            setIsLoading(false);
          }
        } catch (err) {
          console.error("[Model Extraction] API fallback failed:", err);
          setIsLoading(false);
          showErrorToast("Failed to fetch vehicle models");
        }
      };

      fetchFromApi();
    };

    extractModelsFromSpreadsheet();
  }, [
    selectedMakeId,
    renewData.insuranceType,
    makeModelMapping.data,
    niconMake,
    ceiMake,
    token,
  ]);

  /**
   * Initialize vehicle colors on mount
   */
  useEffect(() => {
    // Colors are already initialized from constants, no need to fetch
    console.log("[Vehicle Colors] Initialized from constants");
  }, []);

  return {
    vehicleColor,
    vehicleModel,
    isLoading,
    yearRange: {
      start: YEAR_RANGE.start,
      end: YEAR_RANGE.end,
      years: Array.from(
        { length: YEAR_RANGE.end - YEAR_RANGE.start + 1 },
        (_, i) => YEAR_RANGE.start + i,
      ),
    },
  };
};
